import { useEffect } from "react";
import { getCookieConsent } from "./CookieBanner";

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

let gaLoaded = false;
let pixelLoaded = false;

const loadGA4 = () => {
  if (gaLoaded || !GA4_ID) return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  // Consent Mode v2 — default denied, granted only after explicit consent
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { anonymize_ip: true });
};

const grantAnalytics = () => {
  loadGA4();
  if (window.gtag) {
    window.gtag("consent", "update", { analytics_storage: "granted" });
  }
};

const grantMarketing = () => {
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
  }
  if (pixelLoaded || !PIXEL_ID) return;
  pixelLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
};

const applyConsent = (consent) => {
  if (!consent) return;
  if (consent.analytics) grantAnalytics();
  if (consent.marketing) grantMarketing();
};

const Analytics = () => {
  useEffect(() => {
    applyConsent(getCookieConsent());
    const onConsent = (e) => applyConsent(e.detail);
    window.addEventListener("leovera:cookie-consent", onConsent);
    return () => window.removeEventListener("leovera:cookie-consent", onConsent);
  }, []);
  return null;
};

export default Analytics;

// ---------- E-commerce conversion events (GA4 + Meta Pixel) ----------
// No-op until the visitor has granted analytics/marketing consent (gtag/fbq only
// exist on window once loaded by grantAnalytics()/grantMarketing() above).
export const trackViewItem = (product) => {
  if (!product) return;
  if (window.gtag) {
    window.gtag("event", "view_item", {
      currency: "EUR",
      value: Number(product.price) || 0,
      items: [{ item_id: product.id, item_name: product.name, item_brand: product.brand }],
    });
  }
  if (window.fbq) {
    window.fbq("track", "ViewContent", {
      content_ids: [product.id], content_name: product.name, currency: "EUR", value: Number(product.price) || 0,
    });
  }
};

export const trackAddToCart = (product, qty = 1, unitPrice) => {
  if (!product) return;
  const value = (Number(unitPrice ?? product.price) || 0) * qty;
  if (window.gtag) {
    window.gtag("event", "add_to_cart", {
      currency: "EUR",
      value,
      items: [{ item_id: product.id, item_name: product.name, item_brand: product.brand, quantity: qty }],
    });
  }
  if (window.fbq) {
    window.fbq("track", "AddToCart", { content_ids: [product.id], content_name: product.name, currency: "EUR", value });
  }
};

export const trackBeginCheckout = (items = [], total = 0) => {
  if (window.gtag) {
    window.gtag("event", "begin_checkout", {
      currency: "EUR",
      value: Number(total) || 0,
      items: items.map((i) => ({ item_id: i.product_id, item_name: i.name, quantity: i.quantity })),
    });
  }
  if (window.fbq) {
    window.fbq("track", "InitiateCheckout", { currency: "EUR", value: Number(total) || 0, num_items: items.length });
  }
};

export const trackPurchase = (order) => {
  if (!order) return;
  const value = Number(order.total ?? order.amount_total) || 0;
  const transactionId = order.order_number || order.session_id || "";
  if (window.gtag) {
    window.gtag("event", "purchase", {
      transaction_id: transactionId,
      currency: "EUR",
      value,
      items: (order.items || []).map((i) => ({
        item_id: i.product_id, item_name: i.product_name, quantity: i.quantity, price: Number(i.unit_price) || 0,
      })),
    });
  }
  if (window.fbq) {
    window.fbq("track", "Purchase", { currency: "EUR", value, content_ids: (order.items || []).map((i) => i.product_id) });
  }
};
